import React, { useState, useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import HeadingBanner from "../components/HeadingBanner";
import LeftPanel from "../components/LeftPanel";
import EnhancedAIReportsPanel, {
  ENHANCED_REPORT_KEYS,
  getRequestedReport,
} from "../components/EnhancedAIReports/EnhancedAIReportsPanel";
import { whoami, getEntityTags } from "../services/APIClient";
import { useTeacherNavigation } from "../contexts/TeacherNavigationContext";
import { TEACHER_DASHBOARD_NAVIGATION_IDS } from "../config/teacherDashboardNavigation";
import useReportingConfiguration from "../hooks/useReportingConfiguration";
import TeacherNavigationUnavailable from "../components/TeacherNavigationUnavailable";

const REPORT_NAVIGATION_IDS = {
  [ENHANCED_REPORT_KEYS.NEW_CLASS_INSIGHTS]:
    TEACHER_DASHBOARD_NAVIGATION_IDS.NEW_CLASS_INSIGHTS,
  [ENHANCED_REPORT_KEYS.SCREENING_POINT]:
    TEACHER_DASHBOARD_NAVIGATION_IDS.SCREENING_POINT_REPORT,
  [ENHANCED_REPORT_KEYS.PTM_PARENT_HANDOUT]:
    TEACHER_DASHBOARD_NAVIGATION_IDS.PARENT_TEACHER_MEETING,
};

/**
 * Enhanced AI Reports page (New Class Insights / Screening Point / PTM).
 * Thin wrapper — matches the convention of pages/EOYReportAssistant.js.
 * All real logic lives in components/EnhancedAIReports/.
 */
function EnhancedAIReports() {
  const [searchParams] = useSearchParams();
  const requestedReport = getRequestedReport(searchParams);
  const reportNavigationId = REPORT_NAVIGATION_IDS[requestedReport];
  const { isNavigationEnabled } = useTeacherNavigation();
  const reportingConfiguration = useReportingConfiguration();
  const [teacherName, setTeacherName] = useState("");
  const [teacherId, setTeacherId] = useState(null);
  const [isAuthorised, setIsAuthorised] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    whoami()
      .then((res) => {
        const user = res?.data || res;
        if (user?.stakeholder) {
          setTeacherName(user.stakeholder.name || "");
          setTeacherId(user.stakeholder.id);
          return getEntityTags("teacher", user.stakeholder.id);
        }
      })
      .then((tags) => {
        if (tags) {
          const tag = tags.find(
            (t) => t.tag_definition?.tag_name === "chatbot_teacher_access"
          );
          setIsAuthorised(tag?.tag_value === "ON");
        }
      })
      .catch((err) => console.error("EnhancedAIReports init failed:", err))
      .finally(() => setLoading(false));
  }, []);

  if (!isNavigationEnabled(reportNavigationId)) {
    return <TeacherNavigationUnavailable />;
  }

  const reportCopy = reportingConfiguration[reportNavigationId];

  return (
    <Container fluid>
      <HeadingBanner
        name={teacherName ? `Welcome ${teacherName}!` : "Enhanced AI Reports"}
        isLoggedIn={true}
        cloud={true}
        teacherid={teacherId}
      />
      <Row className="vh-100">
        <LeftPanel />
        <Col
          sm={{ span: 10, offset: 2 }}
          className="eoy-page-bg"
          style={{ paddingTop: "3%" }}
        >
          {!loading && (
            isAuthorised ? (
              <EnhancedAIReportsPanel
                report={requestedReport}
                title={reportCopy.title}
                subheading={reportCopy.subheading}
              />
            ) : (
              <div style={{ textAlign: "center", marginTop: "10%", color: "#666" }}>
                <h3>Access Restricted</h3>
                <p>You do not have permission to access this feature.</p>
              </div>
            )
          )}
        </Col>
      </Row>
    </Container>
  );
}

export default EnhancedAIReports;
